/* Shared UI primitives: avatars, popovers, modals, toasts, and small helpers. */
import {
  useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import * as Icon from '../lib/icons.jsx';

/* ------------------------------------------------------------------- avatar */

export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ------------------------------------------------------------------ emblem */

/**
 * The square mark for a board or a workspace: a picture, an emoji, or initials.
 *
 * ONE STORED VALUE, three renderings. `icon` holds either a URL or an emoji,
 * and which it is, is decided by looking for "://" rather than by a second
 * column saying so - two columns would let both be set at once and leave every
 * consumer to invent its own precedence.
 *
 * Null falls through to initials over the thing's own colour, which is what
 * these surfaces showed before icons existed. So a board that has never been
 * given one looks deliberate rather than empty, and nothing had to be
 * backfilled.
 */
export function Emblem({ name, icon, color, size = '', className = '', title }) {
  const cls = `emblem ${size} ${className}`.trim();
  const label = title ?? name ?? '';

  if (icon && icon.includes('://')) {
    return (
      <img
        className={cls}
        src={icon}
        alt=""
        title={label}
        /* A deleted or expired image must not leave a broken-image glyph where
           the mark was. Same reasoning as Avatar. */
        onError={(event) => { event.currentTarget.style.display = 'none'; }}
      />
    );
  }

  if (icon) {
    return <span className={`${cls} is-emoji`} title={label} aria-hidden="true">{icon}</span>;
  }

  return (
    <span className={cls} style={{ background: color || 'var(--brand)' }} title={label} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/**
 * A person, as a picture if they have uploaded one and as their initials on a
 * coloured disc if not.
 *
 * Both spellings of both fields are accepted on purpose. People reach this
 * component from three different directions - the RPCs return camelCase, plain
 * table reads return the snake_case column names, and `app.user` is its own
 * shape - and the mismatch is not theoretical: `avatarColor` was read here
 * while every caller supplied `avatar_color`, so a colour saved in Settings was
 * written to the database correctly and then never rendered anywhere. Reading
 * both here fixes it for every caller at once, rather than in whichever ones
 * somebody remembers.
 */
/** What the presence dot says out loud, for titles and status lines. */
export const PRESENCE_LABELS = {
  available: 'Available',
  away: 'Away',
  busy: 'Busy',
  leave: 'On leave',
  offline: 'Offline',
};

export function Avatar({ person, size = '', title, presence }) {
  if (!person) {
    return (
      <span className={`avatar ${size}`} style={{ background: 'var(--surface-3)', color: 'var(--muted)' }}>
        ?
      </span>
    );
  }

  const url = person.avatarUrl || person.avatar_url;
  const color = person.avatarColor || person.avatar_color || 'var(--brand)';
  const presenceLabel = presence ? PRESENCE_LABELS[presence] : null;
  const label = title
    ?? `${person.name}${person.title ? ` · ${person.title}` : ''}${presenceLabel ? ` · ${presenceLabel}` : ''}`;

  /* The dot needs a positioned wrapper, so it only exists when presence is
     asked for - every existing <Avatar> keeps its exact DOM. Offline shows a
     hollow dot rather than nothing: "not here" is information too, and its
     absence would make the dot's absence ambiguous. */
  const face = url ? (
    <img
      className={`avatar ${size}`}
      src={url}
      alt={person.name || ''}
      title={presence ? undefined : label}
      // A deleted or expired image must not leave a broken-image glyph where
      // a face was; fall back to the coloured disc.
      onError={(event) => { event.currentTarget.style.display = 'none'; }}
    />
  ) : (
    <span className={`avatar ${size}`} style={{ background: color }} title={presence ? undefined : label}>
      {initials(person.name)}
    </span>
  );

  if (!presence) return face;
  return (
    <span className="avatar-wrap" title={label}>
      {face}
      <span className={`presence-dot ${presence}`} aria-label={presenceLabel} />
    </span>
  );
}

export function AvatarStack({ people, max = 3, size = '' }) {
  const shown = people.slice(0, max);
  const extra = people.length - shown.length;
  return (
    <span className="avatar-stack">
      {shown.map((person) => <Avatar key={person.id} person={person} size={size} />)}
      {extra > 0 && (
        <span className={`avatar avatar-more ${size}`} title={people.slice(max).map((p) => p.name).join(', ')}>
          +{extra}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ popover */

/**
 * Anchored popover. Positions against the trigger's rect, flips up or left when
 * it would overflow, and closes on outside click, Escape, or scroll of an
 * ancestor. Rendered in a portal so board overflow never clips it.
 */
export function Popover({ anchor, open, onClose, children, align = 'start', width, className = '' }) {
  const ref = useRef(null);
  const [position, setPosition] = useState({ top: -9999, left: -9999 });

  useLayoutEffect(() => {
    if (!open || !anchor?.current || !ref.current) return undefined;

    const place = () => {
      const trigger = anchor.current?.getBoundingClientRect();
      const panel = ref.current?.getBoundingClientRect();
      if (!trigger || !panel) return;

      const margin = 8;
      let left = align === 'end' ? trigger.right - panel.width : trigger.left;
      if (align === 'center') left = trigger.left + trigger.width / 2 - panel.width / 2;
      left = Math.min(Math.max(margin, left), window.innerWidth - panel.width - margin);

      let top = trigger.bottom + 6;
      if (top + panel.height > window.innerHeight - margin) {
        const above = trigger.top - panel.height - 6;
        top = above > margin ? above : Math.max(margin, window.innerHeight - panel.height - margin);
      }
      setPosition({ top, left });
    };

    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchor, align]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (ref.current?.contains(event.target)) return;
      if (anchor?.current?.contains(event.target)) return;
      onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose, anchor]);

  if (!open) return null;

  return createPortal(
    <div
      ref={ref}
      className={`popover ${className}`}
      style={{ top: position.top, left: position.left, width }}
      role="dialog"
    >
      {children}
    </div>,
    document.body,
  );
}

/** Popover plus its trigger state, for the common "button opens a menu" case. */
export function useDisclosure(initial = false) {
  const anchor = useRef(null);
  const [open, setOpen] = useState(initial);
  return {
    anchor,
    open,
    setOpen,
    toggle: useCallback(() => setOpen((v) => !v), []),
    close: useCallback(() => setOpen(false), []),
  };
}

export function MenuItem({ icon: IconComponent, children, selected, danger, onClick, ...rest }) {
  return (
    <button
      type="button"
      className={`menu-item${selected ? ' selected' : ''}${danger ? ' danger' : ''}`}
      onClick={onClick}
      {...rest}
    >
      {IconComponent && <IconComponent size={15} />}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
      {selected && <Icon.Check size={14} className="tick" />}
    </button>
  );
}

export const MenuSeparator = () => <div className="menu-sep" />;
export const MenuLabel = ({ children }) => <div className="menu-label">{children}</div>;

/* -------------------------------------------------------------------- modal */

export function Modal({ open, onClose, title, subtitle, children, footer, wide, initialFocus }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && initialFocus?.current) {
      const timer = setTimeout(() => initialFocus.current?.focus(), 30);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open, initialFocus]);

  if (!open) return null;

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-head">
          <div style={{ flex: 1 }}>
            <div className="modal-title">{title}</div>
            {subtitle && <div className="modal-sub">{subtitle}</div>}
          </div>
          <button type="button" className="icon-btn sm" onClick={onClose} aria-label="Close">
            <Icon.X size={16} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Promise-free confirm dialog driven by props. */
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Delete', danger = true }) {
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={`btn ${danger ? 'danger' : 'primary'}`}
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm();
                onClose();
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? <span className="spinner" /> : confirmLabel}
          </button>
        </>
      )}
    >
      <p style={{ color: 'var(--text-2)', fontSize: 13.5 }}>{message}</p>
    </Modal>
  );
}

/* -------------------------------------------------------------------- forms */

export function Field({ label, hint, error, children }) {
  const id = useId();
  return (
    <div className="field">
      {label && <label className="field-label" htmlFor={id}>{label}</label>}
      {typeof children === 'function' ? children(id) : children}
      {error ? <div className="field-error">{error}</div> : hint && <div className="field-hint">{hint}</div>}
    </div>
  );
}

/**
 * A password field with a show/hide toggle.
 *
 * Every password box in the app goes through this: a typo in a masked field is
 * invisible, and the fix everywhere else on the web is the same eye button, so
 * people already know to look for it.
 */
export function PasswordInput({ inputRef, className = 'input', ...rest }) {
  const [visible, setVisible] = useState(false);
  const EyeIcon = visible ? Icon.EyeOff : Icon.Eye;
  return (
    <div className="input-eye-wrap">
      <input
        ref={inputRef}
        className={className}
        type={visible ? 'text' : 'password'}
        {...rest}
      />
      <button
        type="button"
        className="input-eye"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        /* tabIndex -1: tabbing from the password box should land on the next
           field, not on the eye - keyboard users can read what they type. */
        tabIndex={-1}
        onClick={() => setVisible((v) => !v)}
      >
        <EyeIcon size={16} />
      </button>
    </div>
  );
}

export function Select({ value, onChange, options, ...rest }) {
  return (
    <div className="select-wrap">
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <Icon.ChevronDown size={15} />
    </div>
  );
}

export function Checkbox({ checked, onChange, label, ...rest }) {
  return (
    <label className="checkbox">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} {...rest} />
      <span className="box"><Icon.Check size={11} strokeWidth={3} /></span>
      {label && <span>{label}</span>}
    </label>
  );
}

/**
 * On/off switch.
 *
 * A checkbox underneath, so it is focusable, keyboard-operable and announced
 * as a switch without any of it being reimplemented. Used where the change
 * applies immediately rather than on a Save button, which is what tells the
 * user it is a setting and not part of a form.
 */
export function Toggle({ checked, onChange, label, hint, disabled }) {
  return (
    <label className={`toggle${disabled ? ' disabled' : ''}`}>
      <input
        type="checkbox"
        role="switch"
        checked={!!checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span className="track"><span className="knob" /></span>
      {(label || hint) && (
        <span className="toggle-text">
          {label && <span className="toggle-label">{label}</span>}
          {hint && <span className="toggle-hint">{hint}</span>}
        </span>
      )}
    </label>
  );
}

/* ------------------------------------------------------------------- states */

export function EmptyState({ icon: IconComponent = Icon.Inbox, title, text, action }) {
  return (
    <div className="empty">
      <div className="empty-icon"><IconComponent size={22} /></div>
      <div className="empty-title">{title}</div>
      {text && <p className="empty-text">{text}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = 'Loading' }) {
  return (
    <div className="empty">
      <span className="spinner" />
      <span className="empty-text">{label}</span>
    </div>
  );
}

export function Toasts({ toasts, onDismiss }) {
  if (!toasts.length) return null;
  return createPortal(
    <div className="toast-stack">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast ${toast.tone}`} role="status">
          {/* The tone signal. Used to be a coloured left border; the dot says
              it in the same vocabulary as presence. */}
          <span className="toast-dot" aria-hidden="true" />
          <div className="toast-body">{toast.message}</div>
          <button type="button" className="icon-btn sm" onClick={() => onDismiss(toast.id)} aria-label="Dismiss">
            <Icon.X size={13} />
          </button>
        </div>
      ))}
    </div>,
    document.body,
  );
}

/* -------------------------------------------------------------------- misc */

/** Contenteditable-free inline text edit: commits on blur or Enter. */
export function InlineText({ value, onCommit, className = '', placeholder, as = 'input', ...rest }) {
  const [draft, setDraft] = useState(value);
  const dirty = useRef(false);

  useEffect(() => { if (!dirty.current) setDraft(value); }, [value]);

  const commit = () => {
    dirty.current = false;
    const next = draft.trim();
    if (next && next !== value) onCommit(next);
    else setDraft(value);
  };

  const Component = as;
  return (
    <Component
      className={className}
      value={draft}
      placeholder={placeholder}
      onChange={(event) => { dirty.current = true; setDraft(event.target.value); }}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && as === 'input') { event.preventDefault(); event.currentTarget.blur(); }
        if (event.key === 'Escape') { dirty.current = false; setDraft(value); event.currentTarget.blur(); }
      }}
      {...rest}
    />
  );
}

export const BOARD_COLORS = [
  '#5b4bd6', '#2a78d6', '#1baf7a', '#eb6834',
  '#eda100', '#e34948', '#e87ba4', '#4a3aa7',
  '#0b9a5b', '#6b7280',
];

/**
 * `colors` DEFAULTS, and that is not decoration.
 *
 * It used to be required in practice and optional in appearance: the component
 * called colors.map() with no fallback, so a caller that omitted it did not get
 * an empty picker, it threw TypeError mid-render and took the whole screen down
 * to the error boundary. That is exactly what happened when the board menu
 * gained a colour row - the prop was left off, and opening board settings showed
 * "Something broke on this screen" instead.
 *
 * A component with a prop that can only be omitted once is a trap for whoever
 * writes the next call site. The board palette is the right default anyway,
 * since it is what every caller but the label editor passes.
 */
export function ColorSwatchPicker({ value, onChange, colors = BOARD_COLORS }) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {colors.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          aria-label={color}
          style={{
            width: 24, height: 24, borderRadius: 6, background: color, cursor: 'pointer',
            border: value === color ? '2px solid var(--text)' : '2px solid transparent',
            outline: value === color ? '1px solid var(--text)' : 'none',
            outlineOffset: 1,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Is this a phone-sized viewport?
 *
 * Width, not a user-agent sniff or a Capacitor check: the question a layout
 * actually has is how much room it has, and a browser window dragged narrow
 * deserves the same answer as a phone. It also means the phone layout can be
 * developed and verified in a desktop browser.
 *
 * 768px is the tablet-portrait boundary. Below it a horizontally scrolling
 * grid stops being readable; at and above it the table fits, which is why
 * tablets keep the desktop board rather than getting a third layout to
 * maintain.
 *
 * matchMedia rather than a resize listener: the browser only notifies on the
 * boundary being crossed, not on every pixel of a drag.
 */
export const PHONE_QUERY = '(max-width: 767.98px)';

export function useIsPhone() {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = (event) => setPhone(event.matches);
    query.addEventListener('change', onChange);
    // The boundary may have moved between first render and this effect.
    setPhone(query.matches);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return phone;
}

export const LABEL_COLORS = [
  '#00c875', '#fdab3d', '#e2445c', '#579bfc', '#a25ddc', '#037f4c',
  '#ff642e', '#ffcb00', '#0086c0', '#bb3354', '#7f5347', '#c3c1cd',
];
